/**
 * Asking again when the terms have changed (decision 8.14).
 *
 * The login panel says that continuing means agreeing, and signing up records it. When the version
 * in force moves on, this stands in front of the product until the person agrees to the new one.
 *
 * Bumping the version is itself the decision that a change is material - a typo fix leaves it
 * alone - so there is no separate judgement to encode here.
 */

import { Link } from "react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAcceptTerms, useMe } from "@/lib/queries";

export function TermsGate({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const accept = useAcceptTerms();
  const view = me.data;

  // While /me is loading, or when nothing has changed, the product is simply itself.
  if (!view || view.accepted_terms_version === view.terms_version) return <>{children}</>;

  return (
    <>
      {children}
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
        <div className="w-full max-w-md space-y-3 rounded-xl border border-white/10 bg-[#16181d] p-5">
          <h2 className="font-medium">Our terms have changed</h2>
          <p className="text-sm text-muted-foreground">
            The Terms of Service and Privacy Policy were updated on {view.terms_version}. Please read
            them and agree to carry on. Your studies and results are untouched either way.
          </p>
          <p className="text-sm">
            <Link className="underline underline-offset-2" to="/terms" target="_blank">Terms of Service</Link>
            {" · "}
            <Link className="underline underline-offset-2" to="/privacy" target="_blank">Privacy Policy</Link>
          </p>
          {accept.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                That did not save. If the terms changed again while this was open, reload the page.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button disabled={accept.isPending} onClick={() => accept.mutate(view.terms_version)}>
              I agree
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
